import { useState } from "react";
import { Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Shared caption shown directly under the page title on /courts,
 * /availability, and /booking/:id. Includes a single "More info" trigger
 * that opens a Court Rental Inclusions popup with the Standard Court
 * Rental conditions. Per-booking conditions are still rendered inline on
 * the Complete Booking page.
 */
const RentalCaption = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div
        data-testid="rental-caption"
        className="mt-3 mb-4 p-3 rounded-sm bg-[#8cc63f]/5 border border-[#8cc63f]/25 text-xs sm:text-sm text-[#e4e4e7] flex flex-wrap items-start gap-x-3 gap-y-2"
      >
        <p className="flex-1 min-w-[260px] leading-relaxed">
          <span className="font-bold text-[#8cc63f]">COURT RENTAL</span> availability is
          updated daily. Tap{" "}
          <span className="font-semibold text-white">More info</span> to view rental
          conditions.
        </p>
        <button
          type="button"
          data-testid="rental-info-button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#8cc63f] border border-[#8cc63f]/40 rounded-sm hover:bg-[#8cc63f] hover:text-black transition-colors"
        >
          <Info size={12} />
          More info
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="rental-info-dialog"
          className="max-w-lg bg-[#0a0a0a] text-white max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-black tracking-tight flex items-center gap-2">
              <Info size={20} className="text-[#8cc63f]" />
              Court Rental Inclusions
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 text-sm leading-relaxed">
            {/* Box 1: Standard Court Rental */}
            <section
              data-testid="inclusions-standard-box"
              className="rounded-sm border border-white/10 bg-white/[0.02] p-4"
            >
              <h3 className="font-bold text-[#8cc63f] mb-2 uppercase tracking-wide text-xs">
                Standard Court Rental · Conditions
              </h3>
              <ul className="space-y-1.5 list-disc list-inside text-[#e4e4e7] marker:text-[#8cc63f]">
                <li>
                  Good for <span className="font-semibold text-white">8 players</span> with a{" "}
                  <span className="font-semibold text-white">maximum of 15 players</span>.
                </li>
                <li>
                  Each player in excess of 8 must pay{" "}
                  <span className="font-semibold text-white">₱100</span>.
                </li>
                <li>
                  <span className="font-semibold text-white">
                    No entrance fee for guests and visitors.
                  </span>
                </li>
                <li>
                  <span className="font-semibold text-white">Bring your own ball.</span>
                </li>
                <li>
                  <span className="font-semibold text-white">Party trays and group meals</span>{" "}
                  have a{" "}
                  <span className="font-semibold text-white">
                    corkage fee of ₱100 per head
                  </span>{" "}
                  <span className="text-[#a1a1aa]">
                    (players and guests 7 years old and above)
                  </span>.
                </li>
                <li>
                  <span className="font-semibold text-white uppercase">
                    Outside alcoholic beverages is prohibited
                  </span>. Liquors and cocktails are available at{" "}
                  <span className="font-semibold text-white">Assenda cafe</span>.
                </li>
              </ul>
              <div className="mt-3 rounded-sm border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
                <p className="text-[11px] text-[#e4e4e7] leading-relaxed">
                  <span className="inline-flex items-center gap-1 mr-1 text-[10px] font-bold uppercase tracking-wider text-amber-400">
                    ⚠ Note
                  </span>{" "}
                  Guests with a confirmed succeeding booking may arrive during
                  your reserved time without being counted, but if they stay
                  in the court area for more than{" "}
                  <span className="font-semibold text-white">30 minutes</span>{" "}
                  before their booking, the applicable extra pax and corkage
                  fees will apply.
                </p>
              </div>
            </section>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RentalCaption;
